
import { defineComponent, ref, computed, watch } from "vue";
import ViewHeader from "@/components/ViewHeader.vue";
import BenefitInfo from "@/components/BenefitInfo.vue";
import { useStore } from "@/store";
import { useRouter } from "vue-router";
import TextInput from "@/components/Inputs/TextInput.vue";
import { api } from "@/services/Api";
import Swal from "sweetalert2";
import moment from "moment";
import { Coupon, PriceLine } from "@/models/Product";
import _ from "lodash";
import { Benefit, Budget } from "@/models/Order";
import { Voucher, OrderProduct } from "@/models/Order";
import { CouponLimitationType, PriceTypeCategory } from "@/models/Enums";
import OverlayModal from "./OverlayModal.vue";
import RadioInput from '@/components/Inputs/RadioInput.vue';
import { useI18n } from "vue-i18n";

export default defineComponent({
    components: {
        ViewHeader,
        TextInput,
        BenefitInfo,
        OverlayModal,
        RadioInput
    },
    async setup() {
        const { t } = useI18n();
        const store = useStore();
        const router = useRouter();
        const clubId = store.state.clubId;
        const club = store.state.businessClients.find(x => x.id == clubId) ?? null;
        const orderNumber = computed({ get: () => store.state.scanValue, set: (nv) => store.state.scanValue = nv });
        const budget = computed(() => store.state.budget);
        const voucher = computed(() => store.state.voucher);
        const orderFound = ref<boolean | null>(orderNumber.value && budget.value && voucher.value != null ? true : null);
        const budgets = ref<Budget[]>(store.state.budgets);
        const searches = ref(store.state.searches);
        const isMoreCoupons = ref(store.state.isMoreCoupons);
        const isOpenCustomerTypesModal = ref(false);
        const isOpenProductsModal = ref(false);
        const currentOrderProduct = ref<OrderProduct | null>(null);
        const currentCustomerTypeId = ref<number | undefined>();
        const currentProductId = ref<number | undefined>();
        const directChannel = 5;

        function getCurrencyRate(id: number) {
            return (
                store.state.currencyRates.find((x) => x.currencyId == id)?.rate ?? 1
            );
        }

        function isExpired(benefit: Benefit) {
            if (!benefit.expirationDateAvailable) {
                return false;
            }

            return moment(benefit.expirationDate).isBefore(moment());
        }

        async function back() {
            store.commit("setClubId", 0);
            store.state.scanValue = "";
            store.state.voucher = new Voucher();
            store.commit("clearOrder");
            store.state.budget = null;
            const initResponse = await api.withLoader().init();
            if (initResponse.data) {
                store.commit("init", initResponse.data);
            }
            router.push({ name: "club-voucherList" });
            store.commit('setBudgets', []);
            store.commit('setHistorySearches', []);
            store.commit('setIsMoreCoupons', false);
        }

        function manualOrder() {
            store.state.scanValue = "";
            store.state.voucher = new Voucher();
            store.commit("clearOrder");
            store.commit("setClubInOrder", clubId);
            store.state.budget = null;
            router.push({ name: "club-voucherList" });
        }

        const getCouponCustomerTypesByProductId = computed(() => (productId: number, couponId: number) => {
            const product = store.state.products.find(x => x.id == productId);
            if (!product) {
                return null;
            }

            const coupon = store.state.coupons.find(x => x.id == couponId);
            if (!coupon) {
                return null;
            }

            const priceTypeIds = getCorrectPriceTypeId(coupon?.priceTypeIds);

            let filteredPricesByPriceType = product.productPrices
                .filter(price => price.channelId == directChannel && priceTypeIds?.includes(price.priceTypeId));

            let filteredPrices = filteredPricesByPriceType
                    .filter(p => clubId && p.businessClientId === clubId);

            if (filteredPrices.length == 0 && filteredPricesByPriceType.length > 0) {
                filteredPrices = filteredPricesByPriceType
                    .filter(p => p.businessClientTypeId === club?.businessClientTypeId);

                if (filteredPrices.length == 0) {
                    filteredPrices = filteredPricesByPriceType
                        .filter(p => !p.businessClientId && !p.businessClientTypeId);
                }
            }

            const productPriceLines = filteredPrices
                .flatMap(price => price.productPriceLines)
                .map(line => line);
            

            const customerTypes = productPriceLines.filter(pl => coupon?.customerTypes.includes(pl.productCustomerTypeId));
            return customerTypes;
        })

        const getProductsByCoupon = computed(() => (coupon: Coupon | null| undefined) => {
            if (!coupon) {
                return null;
            }

            const priceTypeIds = getCorrectPriceTypeId(coupon?.priceTypeIds);
            const products = store.state.products.filter(x => coupon?.productIds.includes(x.id) 
                && x.productPrices.some(x => priceTypeIds?.includes(x.priceTypeId) 
                && x.channelId == directChannel && x.productPriceLines.some(x => coupon?.customerTypes.includes(x.productCustomerTypeId))));

            if (products.length == 0) {
                return null;
            }

            return products;
        })

        const benefitCoupons = computed(() => (benefit: Benefit, cardCode: string) => {
            return _.chain(voucher.value.products.filter(x => {
                if (x.priceLine.coupon?.couponCode && x.cardCode == cardCode) {
                    return benefit.couponCodes.includes(x.priceLine.coupon?.couponCode);
                }
            })).groupBy(x => x.id).map(x => x).value();
        })

        async function search() {
            if (!orderNumber.value) {
                return;
            }
            if (orderFound.value && !isMoreCoupons.value) {
                const initResponse = await api.withLoader().init();
                if (initResponse.data) {
                    store.commit("init", initResponse.data);
                }
            }            

            // Avoid duplicate search if I have more coupons is on
            if(searches.value.includes(orderNumber.value) && isMoreCoupons.value){
                return;
            }

            const response = await api.withLoader().SearchCoupons(orderNumber.value, store.state.clubId);
            if (response.error) {
                Swal.fire({
                    icon: 'error',
                    html: response.errorMessage
                })
                orderFound.value = isMoreCoupons.value && budgets.value.length > 0 ? true : false;
                return;
            }                                  

            if(response.data?.budget && isMoreCoupons.value){
                budgets.value.push(response.data.budget);
            }                
            else if(response.data?.budget){
                store.commit("clearOrder");
                budgets.value = new Array<Budget>(response.data.budget)
            }        

            store.commit('setBudgets', budgets.value);

            orderFound.value = true;            
            store.commit("setClubInOrder", clubId);  
            addSearchHistory(orderNumber.value);            

            const codes = response.data?.couponAvailabilities.map(x => x.couponCode);
            const coupons = store.state.coupons.filter(x => codes?.includes(x.couponCode)) ?? [];

            const priceLines = Array<PriceLine>();
            coupons.forEach((coupon) => {
                coupon.priceTypeIds = getCorrectPriceTypeId(coupon?.priceTypeIds);

                const filteredPricesByPriceType = store.state.preloaded?.prices.prices.filter(p => 
                    coupon.priceTypeIds.includes(p.priceTypeId)
                    && p.channelId == directChannel
                ) ?? [];

                let filteredPrices = filteredPricesByPriceType
                    .filter(p => clubId && p.businessClientId === clubId)
                    .flatMap(p => p.priceLines)
                    .find(x => coupon.customerTypes.includes(x.id));

                if (!filteredPrices && filteredPricesByPriceType.length > 0) {
                    filteredPrices = filteredPricesByPriceType
                        .filter(p => p.businessClientTypeId === club?.businessClientTypeId)
                        .flatMap(p => p.priceLines)
                        .find(x => coupon.customerTypes.includes(x.id));

                    if (!filteredPrices) {
                        filteredPrices = filteredPricesByPriceType
                            .filter(p => !p.businessClientId && !p.businessClientTypeId)
                            .flatMap(p => p.priceLines)
                            .find(x => coupon.customerTypes.includes(x.id));
                    }
                }

                const pl = _.cloneDeep(filteredPrices);

                if (!pl) {
                    return;
                }

                pl.coupon = _.cloneDeep(coupon);
                pl.couponId = coupon.id;
                pl.discount = 0;
                priceLines.push(pl);
            })

            const newVoucher = !isMoreCoupons.value ? new Voucher() : voucher.value;

            for (const pl of priceLines) {
                if (!pl.coupon) {
                    return;
                }
                const couponPriceTypeIds = pl.coupon.priceTypeIds;
                const availability = response.data?.couponAvailabilities.find(x => x.couponId === pl.couponId && x.customerTypeId === pl.id);
                const budget = response.data?.budget;
                const price = store.state.preloaded?.prices.prices.find(x => 
                    couponPriceTypeIds.includes(x.priceTypeId) 
                    && x.priceLines.map(x => x.id).includes(pl.id)
                );
                if (!price) {
                    return;
                }

                if (!availability) {
                    return;
                }

                if (pl.coupon.limitationType == CouponLimitationType.CustomerQuantity) {
                    pl.coupon.leftLimitationTotal = Math.min(pl.coupon.leftLimitationTotal, availability.qty);
                    pl.quantity = Math.min(pl.coupon.leftLimitationTotal, availability.qty);
                }
                else {
                    pl.coupon.leftLimitationTotal = availability.qty;
                    pl.quantity = availability.qty;
                }
                pl.coupon.ticketLimitation = availability.ticketLimitation;
                pl.quantity = pl.quantity ?? 0;
                pl.quantity >= 1 ? pl.quantity-- : pl.quantity;

                let productId = pl.coupon.productIds.length > 1 ? 0 : availability.productId;
                // TODO: After refactoring, need to remove this condition
                if (productId == 0 && (getProductsByCoupon.value(pl.coupon) ?? []).length == 1) {
                    productId = availability.productId;
                }

                const orderProduct = new OrderProduct(productId, availability.productName, price.channelId, price.id, pl.priceForOne, orderNumber.value)
                    .withPrice(pl)
                    .withDate(moment().format("YYYY-MM-DD"));                

                if(availability.date && productId != 0){
                    orderProduct.withDate(availability.date);
                }

                if (availability.slot && productId != 0) {
                    orderProduct.withSlot(availability.slot);
                    store.commit('setBudget', budget);
                }

                newVoucher.products.push(orderProduct);
            }

            store.commit('setVoucher', newVoucher);

            router.replace({
                name: "clubScanResult",
                params: {
                    clubId: clubId,
                },
            });        
        }

        async function addProductsToOrder(orderProduct: OrderProduct) {
            currentOrderProduct.value = orderProduct;
            isOpenCustomerTypesModal.value = true;
        }

        async function addToOrder(orderProduct: OrderProduct) {
            const prices = store.state.preloaded?.prices.prices.filter(x => orderProduct.priceId == x.id);
            const priceTypes = _.uniq(prices?.map(x => x.priceTypeId));

            if (!store.state.order.priceType) {
                store.state.order.priceType = priceTypes[0];
            }

            const suitablePrices = prices?.filter(p => p.priceTypeId == priceTypes[0]).map(x => x.id) ?? [];

            if(!suitablePrices.includes(orderProduct.priceId)){
                return;
            }    

            const plainClone = _.cloneDeep(orderProduct.priceLine);
            const clonePriceLine: PriceLine = Object.setPrototypeOf(plainClone, PriceLine.prototype);

            const productWithSamePrice = store.state.order.products.find(
                (x) =>
                x.id == orderProduct.id &&
                x.priceLine?.id == clonePriceLine.id &&
                x.date == orderProduct.date &&
                x.slotId == orderProduct.slotId
            );


            orderProduct.priceLine.quantity--;
            clonePriceLine.quantity = 0;

            store.commit("updateOrderProduct", {
                productId: orderProduct.id,
                priceLine: clonePriceLine,
                slot: {
                    id: orderProduct.slotId,
                    startTime: orderProduct.slotStartTime,
                    endTime: orderProduct.slotEndTime
                },
                date: orderProduct.date,
                channelId: orderProduct.channelId,
                priceId: orderProduct.priceId,
                currencyId: store.state.preloaded?.prices.prices.find(x => x.id == orderProduct.priceId)?.currencyId,
                cardCode: orderProduct.cardCode
            });
        }

        function onSelectDate(productId, benefitId, slotId, date, budget: Budget, priceTypeId?: number) {
            store.commit('setBudget', budget);
            store.commit('setBudgets', budgets.value);
            store.commit('setHistorySearches', searches.value);
            store.commit('setIsMoreCoupons', isMoreCoupons.value);

            router.push({
                name: "calendar",
                params: {
                    productId: productId,
                    benefitId: benefitId,
                    slotId: slotId,
                    date: moment(date).format("YYYY-MM-DD"),
                    view: 'week',
                    benefitCardCode: budget.cardCode,
                    priceTypeId: priceTypeId,
                },
            });
        }

        function addSearchHistory(value: string) {
            if(!searches.value.includes(value)){
                searches.value.push(value);
            }                
        }

        async function clearAllCoupons() {
            budgets.value = [];
            searches.value = [];
            store.state.scanValue = "";
            store.state.voucher = new Voucher();
            store.commit("clearOrder");
            store.state.budget = null;
            const initResponse = await api.withLoader().init();
            if (initResponse.data) {
                store.commit("init", initResponse.data);
            }
            store.commit('setBudgets', []);
            store.commit('setHistorySearches', []);
            store.commit('setIsMoreCoupons', false);
            orderFound.value = null;
        }

        watch(isMoreCoupons, (newValue, oldValue) => {
            if(!newValue) {
                clearAllCoupons();
            }
        });

        async function cancel() {
            currentOrderProduct.value = null;
            currentCustomerTypeId.value = undefined;
            isOpenCustomerTypesModal.value = false;
        }
        
        async function save() {
            if(currentOrderProduct.value == null){
                return;
            }

            const filteredPricesByPriceType = store.state.preloaded?.prices.prices.filter(p => p.channelId == directChannel) ?? [];
            const pl = filteredPricesByPriceType.flatMap(p => p.priceLines).find(x => x.id == currentCustomerTypeId.value);

            if (pl) {
                currentOrderProduct.value!.priceLine.name = pl.name;
                currentOrderProduct.value!.priceLine.id = pl.id;
                currentOrderProduct.value!.priceLine.priceForOne = pl.priceForOne;
                currentOrderProduct.value!.priceLine.discount = 0;
            }
            else{
                return;
            }   

            addToOrder(currentOrderProduct.value);
            cancel();
        }

        async function chooseProduct() {
            if(currentProductId.value == null && currentOrderProduct.value == null){
                return;
            }

            const selectedProduct = store.state.products.find(x => x.id == currentProductId.value);

            voucher.value.products.forEach(async (product) => {
                if (product.cardCode == currentOrderProduct.value?.cardCode 
                && product.priceLine.couponId == currentOrderProduct?.value.priceLine.couponId 
                && product.id == 0) {
                    const result = await api
                        .withLoader()
                        .getAvailableSlotForTodayByProductId(selectedProduct?.id ?? 0, currentOrderProduct?.value.priceLine.coupon?.priceTypeIds[0]?? 0);

                    if (!result.error && result.data) {
                        product.withSlot(result.data);
                        product.withDate(moment().format("YYYY-MM-DD"));
                    }

                    product.id = selectedProduct?.id ?? 0;
                    product.name = selectedProduct?.name ?? '';                    
                }
            });

            currentProductId.value = undefined; 
            currentOrderProduct.value = null; 
            isOpenProductsModal.value = false;
        }

        function getCorrectPriceTypeId(priceTypeIds: number[]) {

            for (const priceType of priceTypeIds) {
                const price = store.state.priceTypes.find(x => x.id == priceType);
                if (price && (price.category & PriceTypeCategory.Individual) == PriceTypeCategory.Individual) {
                    return [priceType];
                }
            }

            return priceTypeIds;
        }

        function onProductNameClick(product: OrderProduct){
            if(product.id == 0){
                isOpenProductsModal.value = true; 
                currentOrderProduct.value = product;
            } 
        }

        return {
            club,
            orderNumber,
            back,
            manualOrder,
            search,
            orderFound,
            voucher,
            onSelectDate,
            addProductsToOrder,
            benefitCoupons,
            isExpired,
            getCurrencyRate,
            searches,
            budgets,
            isMoreCoupons,
            clearAllCoupons,
            getCouponCustomerTypesByProductId,
            isOpenCustomerTypesModal,
            save,
            cancel,
            currentCustomerTypeId,
            currentOrderProduct,
            getProductsByCoupon,
            isOpenProductsModal,
            currentProductId,
            chooseProduct,
            onProductNameClick
        };
    },
});
